import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, getDocs, addDoc, query, orderBy, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Wallet, Plus, TrendingUp, History, Loader2, CheckCircle, Clock, Smartphone, CreditCard, Copy, ExternalLink, AlertCircle, Heart, Target, CircleDollarSign, Gift, XCircle, Info, Users, Check, ChevronsUpDown, Upload, X, Image as ImageIcon, PieChart as PieChartIcon, BarChart3, Search, Receipt, ZoomIn, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { logAction } from '../../lib/audit';
import { uploadToCloudinary, isCloudinaryConfigured } from '../../lib/cloudinary';
import { computeStats, fetchUserContributions, isConfirmed, isPending, isRejected, monthlyBreakdown, MONTH_LABELS } from '../../lib/contributions';
import { exportContributionsToExcel } from '../../lib/contributionsExcel';
import { expectedAmountForMember, isEventLinkable, sortForLinking } from '../../lib/events';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover"
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const AnimatedCounter = ({ value }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0; const end = value; const duration = 1500; const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setCount(Math.floor(end * (1 - Math.pow(1 - progress, 3))));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value]);
  return <>{count.toLocaleString()}</>;
};

// Types par défaut (fallback si pas configuré)
const defaultContributionTypes = [
  { id: 'cotisation', label: 'Cotisation', icon: '📅', color: 'from-blue-500 to-cyan-500' },
  { id: 'don', label: 'Don', icon: '💝', color: 'from-pink-500 to-rose-500' },
  { id: 'zakat', label: 'Zakat', icon: '🌙', color: 'from-emerald-500 to-green-500' },
  { id: 'sadaqa', label: 'Sadaqa', icon: '✨', color: 'from-amber-500 to-yellow-500' },
  { id: 'autre', label: 'Autre', icon: '🎁', color: 'from-purple-500 to-violet-500' }
];

// Catégorie générique pour les cotisations rattachées à un événement.
const EVENT_CATEGORY = { id: 'evenement', label: 'Événements', icon: '🎯', color: 'from-violet-500 to-purple-600' };

// Palette (hex) pour les graphiques recharts, indexée par position.
const CHART_COLORS = ['#10b981', '#3b82f6', '#ec4899', '#f59e0b', '#8b5cf6', '#06b6d4', '#ef4444', '#14b8a6', '#a855f7'];

// Filtres de statut pour l'historique.
const STATUS_FILTERS = [
  { id: 'all', label: 'Tout' },
  { id: 'confirmed', label: 'Confirmés' },
  { id: 'pending', label: 'En attente' },
  { id: 'rejected', label: 'Rejetés' }
];

export default function Contributions() {
  const { user, userData, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contributions, setContributions] = useState([]);
  const [stats, setStats] = useState({ total: 0, objectiveTotal: 0, thisMonth: 0, pending: 0, pendingAmount: 0 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [progressValue, setProgressValue] = useState(0);
  const [newContribution, setNewContribution] = useState({ amount: '', type: '', description: '', paymentMethod: 'wave', transactionId: '', phoneNumber: '', eventId: '' });

  // Reçu de paiement (capture Wave / Orange Money)
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState('');

  const clearReceipt = () => {
    if (receiptPreview) URL.revokeObjectURL(receiptPreview);
    setReceiptFile(null);
    setReceiptPreview('');
  };

  const handleReceiptSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Choisissez une image (capture du paiement).'); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error('Image trop lourde (max 8 Mo).'); return; }
    if (receiptPreview) URL.revokeObjectURL(receiptPreview);
    setReceiptFile(file);
    setReceiptPreview(URL.createObjectURL(file));
  };

  // Événements avec collecte active (pour affecter une cotisation)
  const [events, setEvents] = useState([]);

  // États pour la gestion admin
  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [openMemberSelect, setOpenMemberSelect] = useState(false);

  // Types de contribution dynamiques
  const [contributionTypes, setContributionTypes] = useState(defaultContributionTypes);

  // Objectif annuel personnalisé (Sass Annuel)
  const [objectifAnnuel, setObjectifAnnuel] = useState(100000);
  const [settings, setSettings] = useState({ annualGoal: 100000 });

  // Filtres de l'historique + visionneuse de reçu
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [receiptViewUrl, setReceiptViewUrl] = useState(null);
  const [exporting, setExporting] = useState(false);

  const paymentMethods = [
    { value: 'wave', label: 'Wave', icon: '/wave.png', color: 'from-blue-500 to-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20', isImage: true },
    { value: 'orange_money', label: 'Orange Money', icon: '/orange money.png', color: 'from-orange-500 to-orange-600', bg: 'bg-orange-50 dark:bg-orange-900/20', isImage: true },
    { value: 'cash', label: 'Espèces', icon: '💵', color: 'from-green-500 to-green-600', bg: 'bg-green-50 dark:bg-green-900/20', isImage: false }
  ];

  const quickAmounts = [150, 1140, 2500, 5000, 10000];

  useEffect(() => {
    fetchContributions();
    fetchPaymentConfig();
    fetchContributionTypes();
    fetchMemberAnnualGoal();
    fetchFundraisingEvents();

    // Initialiser le membre sélectionné avec l'utilisateur courant
    if (user) {
      setSelectedMemberId(user.uid);
    }

    // Charger les membres si admin
    if (isAdmin) {
      fetchMembers();
    }
  }, [user, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setTimeout(() => setProgressValue(Math.min((stats.objectiveTotal / objectifAnnuel) * 100, 100)), 500); }, [stats.objectiveTotal, objectifAnnuel]);

  // Ouverture pré-pointée depuis la page Événements (?event=<id>) :
  // ouvre le formulaire avec l'événement déjà sélectionné et le montant pré-rempli.
  const eventParam = searchParams.get('event');
  useEffect(() => {
    if (!eventParam || !events.length) return;
    const ev = events.find((e) => e.id === eventParam);
    if (ev) {
      const expected = expectedAmountForMember(ev, userData);
      setNewContribution((prev) => ({
        ...prev,
        eventId: ev.id,
        amount: expected > 0 ? String(expected) : prev.amount
      }));
      setDialogOpen(true);
    }
    // Nettoyer le paramètre pour éviter une réouverture au rafraîchissement.
    searchParams.delete('event');
    setSearchParams(searchParams, { replace: true });
  }, [eventParam, events]); // eslint-disable-line react-hooks/exhaustive-deps

  // Charger les événements AVEC collecte active et non expirés (passés depuis
  // moins d'une semaine). Les événements expirés disparaissent automatiquement.
  const fetchFundraisingEvents = async () => {
    try {
      const snap = await getDocs(collection(db, 'events'));
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((e) => e.fundraising?.enabled && isEventLinkable(e));
      setEvents(sortForLinking(list));
    } catch (error) {
      console.error('Error fetching fundraising events:', error);
    }
  };

  const fetchMembers = async () => {
    try {
      const q = query(collection(db, 'users'), orderBy('fullName'));
      const snapshot = await getDocs(q);
      const membersData = snapshot.docs.map(doc => ({
        id: doc.id,
        fullName: doc.data().fullName || doc.data().email,
        email: doc.data().email,
        sassCategory: doc.data().sassCategory || null
      }));
      setMembers(membersData);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  // Charger les types de contribution personnalisés
  const fetchContributionTypes = async () => {
    try {
      const typesRef = doc(db, 'settings', 'contribution_types');
      const typesSnap = await getDoc(typesRef);
      if (typesSnap.exists() && typesSnap.data().types?.length > 0) {
        setContributionTypes(typesSnap.data().types);
        // Définir le premier type comme défaut si pas encore sélectionné
        if (!newContribution.type) {
          setNewContribution(prev => ({ ...prev, type: typesSnap.data().types[0].id }));
        }
      } else {
        // Utiliser les types par défaut
        if (!newContribution.type) {
          setNewContribution(prev => ({ ...prev, type: 'cotisation' }));
        }
      }
    } catch (error) {
      console.error('Error fetching contribution types:', error);
    }
  };

  // Charger l'objectif annuel personnalisé du membre (Sass Annuel)
  const fetchMemberAnnualGoal = async () => {
    if (!user) return;
    try {
      // D'abord récupérer les paramètres généraux (objectif par défaut)
      const settingsRef = doc(db, 'settings', 'general');
      const settingsSnap = await getDoc(settingsRef);
      let defaultGoal = 100000;
      if (settingsSnap.exists()) {
        defaultGoal = settingsSnap.data().annualGoal || 100000;
        setSettings(settingsSnap.data());
      }

      // Ensuite vérifier si le membre a un objectif personnalisé
      const targetUserId = isAdmin ? selectedMemberId : user.uid; // Utiliser l'ID sélectionné si admin (mais attention au contexte d'affichage global)
      // Pour l'affichage global des stats perso, on garde user.uid. 

      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists() && userSnap.data().annualGoal) {
        setObjectifAnnuel(userSnap.data().annualGoal);
      } else {
        setObjectifAnnuel(defaultGoal);
      }
    } catch (error) {
      console.error('Error fetching member annual goal:', error);
    }
  };

  const fetchPaymentConfig = async () => {
    try { const configDoc = await getDoc(doc(db, 'settings', 'payment_config')); if (configDoc.exists()) setPaymentConfig(configDoc.data()); }
    catch (error) { console.error('Error:', error); }
  };

  const fetchContributions = async () => {
    if (!user) return;
    try {
      const data = await fetchUserContributions(user.uid);
      setContributions(data);
      // Stats centralisées (total et "ce mois" uniquement sur les cotisations VALIDÉES)
      const { total, objectiveTotal, thisMonth, pendingCount, pendingAmount } = computeStats(data);
      setStats({ total, objectiveTotal, thisMonth, pending: pendingCount, pendingAmount });
    } catch (error) { console.error('Error:', error); } finally { setLoading(false); }
  };

  const handleCreateContribution = async (e) => {
    e.preventDefault(); setCreating(true);
    try {
      // Validation : pour Wave et Orange Money, au moins l'un des deux champs doit être rempli
      if (newContribution.paymentMethod !== 'cash' && !newContribution.transactionId && !newContribution.phoneNumber) {
        toast.error('Veuillez fournir le numéro de transaction ou le numéro de téléphone utilisé pour le paiement');
        setCreating(false);
        return;
      }

      // Déterminer l'utilisateur cible et son nom
      let targetUserId = user.uid;
      let targetUserName = userData?.fullName || 'Membre';

      // Si admin et un autre membre est sélectionné
      if (isAdmin && selectedMemberId && selectedMemberId !== user.uid) {
        const selectedMember = members.find(m => m.id === selectedMemberId);
        if (selectedMember) {
          targetUserId = selectedMember.id;
          targetUserName = selectedMember.fullName;
        }
      }

      // Upload du reçu (capture du paiement) sur Cloudinary, si fourni.
      let receiptUrl = null;
      let receiptPublicId = null;
      if (receiptFile) {
        if (!isCloudinaryConfigured()) {
          toast.error('Envoi du reçu indisponible (Cloudinary non configuré).');
          setCreating(false);
          return;
        }
        try {
          const res = await uploadToCloudinary(receiptFile, { folder: 'receipts' });
          receiptUrl = res.secureUrl;
          receiptPublicId = res.publicId;
        } catch (err) {
          console.error('Upload reçu échoué:', err);
          toast.error('Échec de l\'envoi du reçu. Réessayez ou retirez l\'image.');
          setCreating(false);
          return;
        }
      }

      // Enregistrement de la contribution avec les infos de paiement
      const linkedEvent = events.find((ev) => ev.id === newContribution.eventId) || null;
      await addDoc(collection(db, 'contributions'), {
        userId: targetUserId,
        userName: targetUserName,
        createdByAdmin: isAdmin && targetUserId !== user.uid, // Marquer si créé par un admin pour autrui
        adminId: isAdmin && targetUserId !== user.uid ? user.uid : null,
        amount: parseInt(newContribution.amount),
        // Cotisation d'événement : type générique 'evenement' (le détail est dans eventTitle).
        type: newContribution.eventId ? 'evenement' : newContribution.type,
        description: newContribution.description,
        paymentMethod: newContribution.paymentMethod,
        transactionId: newContribution.transactionId || null,
        phoneNumber: newContribution.phoneNumber || null,
        // Affectation à un événement / collecte (optionnel)
        eventId: linkedEvent?.id || null,
        eventTitle: linkedEvent?.title || null,
        // Reçu de paiement (capture Wave / Orange Money)
        receiptUrl,
        receiptPublicId,
        status: 'pending',
        createdAt: serverTimestamp()
      });

      const message = isAdmin && targetUserId !== user.uid
        ? `Cotisation enregistrée pour ${targetUserName} !`
        : 'Cotisation enregistrée ! Un administrateur validera votre paiement après vérification.';

      await logAction(user.uid, user.email, 'CREATE_CONTRIBUTION', `Contribution: ${newContribution.amount} F (${newContribution.type})`, {
        amount: newContribution.amount,
        type: newContribution.type,
        targetUser: targetUserName
      });

      toast.success(message, { duration: 5000 });
      setDialogOpen(false);
      setNewContribution({ amount: '', type: contributionTypes[0]?.id || 'cotisation', description: '', paymentMethod: 'wave', transactionId: '', phoneNumber: '', eventId: '' });
      clearReceipt();

      // Si on a ajouté pour soi-même, rafraîchir la liste
      if (targetUserId === user.uid) {
        fetchContributions();
      }
    } catch (error) { toast.error('Erreur lors de l\'enregistrement'); } finally { setCreating(false); }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copié'); };
  const getType = (t) => contributionTypes.find(c => c.id === t) || contributionTypes[contributionTypes.length - 1] || { id: 'autre', label: 'Autre', icon: '🎁', color: 'from-purple-500 to-violet-500' };
  const getMethod = (m) => paymentMethods.find(p => p.value === m) || paymentMethods[2];

  // Catégorie « cartographie » d'une contribution : les cotisations rattachées à
  // un événement sont regroupées sous une catégorie unique « Événements ».
  const categoryKey = (c) => (c.eventId ? EVENT_CATEGORY.id : c.type);
  const categoryMeta = (c) => (c.eventId ? EVENT_CATEGORY : getType(c.type));

  const currentYear = new Date().getFullYear();

  // Répartition par type de contribution (sur les cotisations VALIDÉES uniquement).
  const typeBreakdown = useMemo(() => {
    const map = new Map();
    contributions.filter(isConfirmed).forEach((c) => {
      const key = categoryKey(c);
      if (!map.has(key)) {
        const meta = categoryMeta(c);
        map.set(key, { key, label: meta.label, icon: meta.icon, color: meta.color, total: 0, count: 0 });
      }
      const entry = map.get(key);
      entry.total += c.amount || 0;
      entry.count += 1;
    });
    const list = Array.from(map.values()).sort((a, b) => b.total - a.total);
    const grandTotal = list.reduce((s, e) => s + e.total, 0);
    return list.map((e, i) => ({ ...e, color: e.color, hex: CHART_COLORS[i % CHART_COLORS.length], pct: grandTotal ? Math.round((e.total / grandTotal) * 100) : 0 }));
  }, [contributions, contributionTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Évolution mensuelle des cotisations validées pour l'année en cours.
  const monthlyData = useMemo(
    () => monthlyBreakdown(contributions, currentYear).map((v, i) => ({ month: MONTH_LABELS[i], total: v })),
    [contributions] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Historique filtré (statut + type + recherche texte).
  const filteredContributions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contributions.filter((c) => {
      if (statusFilter === 'confirmed' && !isConfirmed(c)) return false;
      if (statusFilter === 'pending' && !isPending(c)) return false;
      if (statusFilter === 'rejected' && !isRejected(c)) return false;
      if (typeFilter !== 'all' && categoryKey(c) !== typeFilter) return false;
      if (q) {
        const label = c.eventId ? (c.eventTitle || 'Événement') : getType(c.type).label;
        const hay = `${label} ${c.description || ''} ${c.amount || ''} ${c.transactionId || ''} ${c.phoneNumber || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [contributions, statusFilter, typeFilter, search, contributionTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Types réellement présents dans l'historique (pour les puces de filtre).
  const presentTypeKeys = useMemo(() => {
    const keys = new Set(contributions.map(categoryKey));
    return keys;
  }, [contributions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Export Excel (.xlsx) de l'historique personnel (lignes actuellement filtrées).
  const exportExcel = async () => {
    const rows = filteredContributions;
    if (!rows.length) { toast.error('Aucune contribution à exporter.'); return; }
    setExporting(true);
    try {
      await exportContributionsToExcel(rows, {
        contributionTypes,
        paymentMethods,
        memberName: userData?.fullName || 'Membre',
        year: currentYear
      });
      toast.success(`${rows.length} contribution(s) exportée(s) en Excel.`);
    } catch (err) {
      console.error('Export Excel échoué:', err);
      toast.error("Échec de l'export Excel.");
    } finally {
      setExporting(false);
    }
  };

  const hasActiveFilters = statusFilter !== 'all' || typeFilter !== 'all' || search.trim() !== '';

  return (
    <div className="space-y-8" data-testid="contributions-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl md:rounded-3xl bg-gradient-to-br from-emerald-600 via-green-600 to-teal-700 p-4 md:p-8 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptMCAwdi02aC02djZoNnptLTYgNmgtNnY2aDZ2LTZ6bTYgMHY2aDZ2LTZoLTZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50" />
        <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl hidden md:block" />
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-6">
          <div className="space-y-1 md:space-y-3">
            <div className="flex items-center gap-2"><Heart className="h-4 w-4 md:h-5 md:w-5 text-emerald-200" /><span className="text-emerald-200 text-xs md:text-sm font-medium">Soutenez le Dahira</span></div>
            <h1 className="text-2xl md:text-4xl font-bold">Cotisations & Dons</h1>
            <p className="text-emerald-100/80 max-w-md text-sm hidden md:block">Contribuez au développement de la communauté.</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild><Button size="sm" className="bg-white text-emerald-600 hover:bg-white/90 shadow-lg gap-1 md:gap-2 text-xs md:text-sm" data-testid="add-contribution-btn"><Plus className="h-3 w-3 md:h-4 md:w-4" /><span className="hidden md:inline">Nouvelle</span> contribution</Button></DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
              <DialogHeader className="flex-shrink-0"><DialogTitle className="flex items-center gap-2"><Heart className="h-5 w-5 text-emerald-500" />Nouvelle contribution</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateContribution} className="space-y-4 overflow-y-auto flex-1 pr-2">

                {/* Sélection du membre pour les admins */}
                {isAdmin && (
                  <div className="space-y-2 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
                    <Label className="flex items-center gap-2 text-primary font-bold">
                      <Users className="h-4 w-4" />
                      Enregistrer pour
                    </Label>
                    <Popover open={openMemberSelect} onOpenChange={setOpenMemberSelect}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openMemberSelect}
                          className="w-full justify-between bg-white dark:bg-slate-950"
                        >
                          {selectedMemberId
                            ? members.find((member) => member.id === selectedMemberId)?.fullName
                            : "Sélectionner un membre..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                        <Command>
                          <CommandInput placeholder="Rechercher un membre..." />
                          <CommandList>
                            <CommandEmpty>Aucun membre trouvé.</CommandEmpty>
                            <CommandGroup>
                              {members.map((member) => (
                                <CommandItem
                                  key={member.id}
                                  value={member.fullName}
                                  onSelect={() => {
                                    setSelectedMemberId(member.id);
                                    setOpenMemberSelect(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      selectedMemberId === member.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {member.fullName} {member.id === user.uid ? '(Moi)' : ''}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                <div className="space-y-2"><Label>Montant rapide</Label><div className="flex flex-wrap gap-2">{quickAmounts.map(a => <Button key={a} type="button" variant={newContribution.amount === String(a) ? "default" : "outline"} size="sm" onClick={() => setNewContribution({ ...newContribution, amount: String(a) })} className="rounded-full">{a.toLocaleString()} F</Button>)}</div></div>
                <div className="space-y-2"><Label>Montant (FCFA)</Label><div className="relative"><CircleDollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" /><Input type="number" value={newContribution.amount} onChange={(e) => setNewContribution({ ...newContribution, amount: e.target.value })} placeholder="Ex : 5000" min="100" required className="pl-10 text-lg font-semibold placeholder:font-normal placeholder:text-muted-foreground/40" data-testid="contribution-amount-input" /></div></div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                    {contributionTypes.map(t => {
                      // Un type est actif seulement si aucun événement n'est sélectionné.
                      const selected = !newContribution.eventId && newContribution.type === t.id;
                      return (
                        <button key={t.id} type="button" onClick={() => setNewContribution({ ...newContribution, type: t.id, eventId: '' })} className={`p-2 rounded-xl border-2 text-center transition-all ${selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                          <span className="text-lg block">{t.icon}</span>
                          <span className="text-[9px] font-medium">{t.label}</span>
                        </button>
                      );
                    })}
                    {/* Événements avec collecte : sélectionnables comme un type (exclusif). Pré-remplit le montant attendu selon le profil. */}
                    {events.map((ev) => {
                      const selected = newContribution.eventId === ev.id;
                      return (
                        <button
                          key={ev.id}
                          type="button"
                          data-testid={`contribution-event-${ev.id}`}
                          title={ev.title}
                          onClick={() => {
                            if (selected) {
                              // Désélection : on revient à une cotisation générale (1er type).
                              setNewContribution({ ...newContribution, eventId: '', type: contributionTypes[0]?.id || 'cotisation' });
                              return;
                            }
                            const targetMember = (isAdmin && selectedMemberId && selectedMemberId !== user?.uid)
                              ? members.find((m) => m.id === selectedMemberId)
                              : userData;
                            const expected = expectedAmountForMember(ev, targetMember);
                            setNewContribution({
                              ...newContribution,
                              eventId: ev.id,
                              type: '', // exclusif : aucun type générique actif
                              amount: expected > 0 ? String(expected) : newContribution.amount
                            });
                          }}
                          className={`p-2 rounded-xl border-2 text-center transition-all ${selected ? 'border-violet-500 bg-violet-500/10' : 'border-border hover:border-violet-400/60'}`}
                        >
                          <Target className={`h-[18px] w-[18px] mx-auto ${selected ? 'text-violet-600' : 'text-violet-500'}`} />
                          <span className="text-[9px] font-medium block truncate leading-tight mt-0.5">{ev.title}</span>
                        </button>
                      );
                    })}
                  </div>
                  {newContribution.eventId && (
                    <p className="text-xs text-violet-600 dark:text-violet-400 flex items-start gap-1.5">
                      <Target className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>Cotisation rattachée à <strong>{events.find((e) => e.id === newContribution.eventId)?.title}</strong> — montant pré-rempli selon le profil (modifiable). Comptée dans l'encaissé <strong>une fois validée</strong>.</span>
                    </p>
                  )}
                </div>
                <div className="space-y-2"><Label>Paiement</Label><div className="grid grid-cols-3 gap-2">{paymentMethods.map(m => <button key={m.value} type="button" onClick={() => setNewContribution({ ...newContribution, paymentMethod: m.value })} className={`p-3 rounded-xl border-2 text-center transition-all ${newContribution.paymentMethod === m.value ? `border-primary ${m.bg}` : 'border-border'}`} data-testid={`payment-method-${m.value}`}>{m.isImage ? <img src={m.icon} alt={m.label} className="h-6 w-auto mx-auto mb-1 object-contain" /> : <span className="text-xl block mb-1">{m.icon}</span>}<span className="text-xs font-medium">{m.label}</span></button>)}</div></div>
                {/* Infos de paiement pour Wave et Orange Money */}
                {newContribution.paymentMethod !== 'cash' && (
                  <div className="space-y-3 p-3 rounded-xl bg-muted/50 border">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      {newContribution.paymentMethod === 'wave' ? <img src="/wave.png" alt="Wave" className="h-4" /> : <img src="/orange money.png" alt="OM" className="h-4" />}
                      Infos paiement
                    </div>
                    <p className="text-xs text-muted-foreground">Effectuez d'abord votre paiement, puis renseignez les informations.</p>
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <Label htmlFor="transactionId" className="text-xs">N° de transaction</Label>
                        <Input id="transactionId" value={newContribution.transactionId} onChange={(e) => setNewContribution({ ...newContribution, transactionId: e.target.value })} placeholder="Ex: TXN123456789" className="font-mono h-9" data-testid="transaction-id-input" />
                      </div>
                      <div className="text-xs text-center text-muted-foreground">ou</div>
                      <div className="space-y-1">
                        <Label htmlFor="phoneNumber" className="text-xs">N° de téléphone utilisé</Label>
                        <Input id="phoneNumber" type="tel" value={newContribution.phoneNumber} onChange={(e) => setNewContribution({ ...newContribution, phoneNumber: e.target.value })} placeholder="Ex: 77 123 45 67" className="h-9" data-testid="phone-number-input" />
                      </div>

                      {/* Reçu / capture du paiement */}
                      <div className="space-y-1 pt-1">
                        <Label className="text-xs flex items-center gap-1.5"><ImageIcon className="h-3.5 w-3.5" /> Reçu du paiement <span className="text-muted-foreground font-normal">(recommandé)</span></Label>
                        {receiptPreview ? (
                          <div className="relative inline-block">
                            <img src={receiptPreview} alt="Aperçu du reçu" className="max-h-40 rounded-lg border object-contain" />
                            <button type="button" onClick={clearReceipt} className="absolute -right-2 -top-2 rounded-full bg-destructive p-1 text-white shadow hover:bg-destructive/90" aria-label="Retirer le reçu">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <label className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-border py-4 text-center transition-colors hover:border-primary/50 hover:bg-muted/40" data-testid="receipt-upload">
                            <Upload className="h-5 w-5 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">Ajouter une capture (Wave / Orange Money)</span>
                            <input type="file" accept="image/*" className="hidden" onChange={handleReceiptSelect} />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {newContribution.paymentMethod === 'cash' && <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 py-2"><CreditCard className="h-4 w-4 text-green-600" /><AlertDescription className="text-green-800 dark:text-green-200 text-xs"><strong>Espèces:</strong> Remettez au trésorier.</AlertDescription></Alert>}
                <Input value={newContribution.description} onChange={(e) => setNewContribution({ ...newContribution, description: e.target.value })} placeholder="Note (optionnel)" className="h-9" data-testid="contribution-description-input" />
                <Button type="submit" className="w-full bg-gradient-to-r from-emerald-500 to-green-600 h-11" disabled={creating} data-testid="submit-contribution-btn">
                  {creating ? (
                    <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Enregistrement...</>
                  ) : (
                    <>
                      Enregistrer
                      {isAdmin && selectedMemberId && selectedMemberId !== user.uid ? ` pour ${members.find(m => m.id === selectedMemberId)?.fullName?.split(' ')[0]}` : ''}
                      {newContribution.amount && ` • ${parseInt(newContribution.amount).toLocaleString()} F`}
                    </>
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="grid grid-cols-3 gap-2 md:gap-4">
        {paymentMethods.map((m, i) => <Card key={m.value} className={`${m.bg} border-2 overflow-hidden group hover:shadow-lg transition-all`}><CardContent className="p-3 md:p-5 flex flex-col md:flex-row items-center gap-2 md:gap-4 relative"><div className={`absolute -right-8 -top-8 w-24 h-24 bg-gradient-to-br ${m.color} opacity-10 rounded-full blur-2xl group-hover:scale-150 transition-transform`} /><div className={`w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl ${m.isImage ? 'bg-white' : `bg-gradient-to-br ${m.color}`} flex items-center justify-center shadow-lg p-1.5 md:p-2`}>{m.isImage ? <img src={m.icon} alt={m.label} className="h-full w-full object-contain" /> : <span className="text-lg md:text-2xl text-white">{m.icon}</span>}</div><div className="relative text-center md:text-left"><h4 className="font-semibold text-sm md:text-lg">{m.label}</h4><p className="text-[10px] md:text-sm text-muted-foreground hidden md:block">{m.value === 'wave' ? 'Mobile money' : m.value === 'orange_money' ? 'Mobile money' : 'Remise au trésorier'}</p></div></CardContent></Card>)}
      </div>

      {/* Pending Alert */}
      {stats.pending > 0 && (
        <Alert className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
          <Clock className="h-5 w-5 text-amber-600" />
          <AlertDescription className="text-amber-800 dark:text-amber-200">
            <strong>Vous avez {stats.pending} cotisation(s) en attente de validation</strong> ({stats.pendingAmount.toLocaleString()} FCFA)
            <br />
            <span className="text-sm">Un administrateur doit valider votre paiement avant qu'il soit comptabilisé.</span>
          </AlertDescription>
        </Alert>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6">
        <Card className="lg:col-span-3 relative overflow-hidden"><div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-transparent" /><CardContent className="p-4 md:p-6 relative"><div className="grid grid-cols-3 gap-3 md:gap-6"><div className="space-y-1 md:space-y-2"><div className="flex items-center gap-1 md:gap-2 text-muted-foreground"><Wallet className="h-3 w-3 md:h-4 md:w-4" /><span className="text-[10px] md:text-sm">Total</span></div><p className="text-xl md:text-4xl font-bold"><AnimatedCounter value={stats.total} /> <span className="text-xs md:text-lg font-normal text-muted-foreground">F</span></p></div><div className="space-y-1 md:space-y-2"><div className="flex items-center gap-1 md:gap-2 text-muted-foreground"><TrendingUp className="h-3 w-3 md:h-4 md:w-4" /><span className="text-[10px] md:text-sm">Ce mois</span></div><p className="text-xl md:text-4xl font-bold text-emerald-600">+<AnimatedCounter value={stats.thisMonth} /> <span className="text-xs md:text-lg font-normal text-muted-foreground">F</span></p></div><div className="space-y-1 md:space-y-2"><div className="flex items-center gap-1 md:gap-2 text-muted-foreground"><History className="h-3 w-3 md:h-4 md:w-4" /><span className="text-[10px] md:text-sm">Trans.</span></div><p className="text-xl md:text-4xl font-bold"><AnimatedCounter value={contributions.length} /></p></div></div></CardContent></Card>
        <Card className="relative overflow-hidden"><div className="absolute -right-8 -top-8 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl" /><CardContent className="p-4 md:p-6 relative space-y-3 md:space-y-4"><div className="flex items-center justify-between"><div className="flex items-center gap-1 md:gap-2"><Target className="h-4 w-4 md:h-5 md:w-5 text-emerald-500" /><span className="font-medium text-sm md:text-base">Objectif</span></div><Badge variant="secondary" className="font-mono text-xs">{Math.round(progressValue)}%</Badge></div><Progress value={progressValue} className="h-2 md:h-3" /><div className="flex justify-between text-xs md:text-sm"><span className="font-semibold">{stats.objectiveTotal.toLocaleString()}</span><span className="text-muted-foreground">/ {objectifAnnuel.toLocaleString()}</span></div></CardContent></Card>
      </div>

      {/* Cartographie : répartition par type + évolution mensuelle */}
      {!loading && stats.total > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Répartition par type de contribution */}
          <Card className="overflow-hidden">
            <CardHeader className="border-b bg-muted/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center"><PieChartIcon className="h-5 w-5 text-white" /></div>
                <div><CardTitle>Répartition par type</CardTitle><CardDescription>Vos contributions validées par catégorie</CardDescription></div>
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-6">
              <div className="flex flex-col sm:flex-row items-center gap-4 md:gap-6">
                <div className="w-40 h-40 shrink-0 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={typeBreakdown} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={2} dataKey="total" nameKey="label">
                        {typeBreakdown.map((entry) => <Cell key={entry.key} fill={entry.hex} />)}
                      </Pie>
                      <Tooltip formatter={(value) => [`${value.toLocaleString()} F`, 'Montant']} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-xs text-muted-foreground">Total</span>
                    <span className="text-sm font-bold">{stats.total.toLocaleString()} F</span>
                  </div>
                </div>
                <div className="flex-1 w-full space-y-2.5">
                  {typeBreakdown.map((e) => (
                    <button
                      key={e.key}
                      type="button"
                      onClick={() => setTypeFilter((prev) => (prev === e.key ? 'all' : e.key))}
                      className={`w-full text-left rounded-xl p-2.5 transition-colors ${typeFilter === e.key ? 'bg-muted ring-1 ring-primary/30' : 'hover:bg-muted/60'}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="flex items-center gap-2 text-sm font-medium min-w-0"><span className="text-base">{e.icon}</span><span className="truncate">{e.label}</span><span className="text-xs text-muted-foreground shrink-0">×{e.count}</span></span>
                        <span className="text-sm font-bold whitespace-nowrap">{e.total.toLocaleString()} F</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full" style={{ width: `${e.pct}%`, backgroundColor: e.hex }} /></div>
                        <span className="text-[11px] text-muted-foreground w-8 text-right">{e.pct}%</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Évolution mensuelle */}
          <Card className="overflow-hidden">
            <CardHeader className="border-b bg-muted/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center"><BarChart3 className="h-5 w-5 text-white" /></div>
                <div><CardTitle>Évolution {currentYear}</CardTitle><CardDescription>Cotisations validées mois par mois</CardDescription></div>
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-6">
              <div className="w-full h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} interval={0} />
                    <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} width={44} />
                    <Tooltip formatter={(value) => [`${value.toLocaleString()} F`, 'Validé']} cursor={{ fill: 'hsl(var(--muted))' }} />
                    <Bar dataKey="total" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={32} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* History */}
      <Card className="overflow-hidden">
        <CardHeader className="border-b bg-muted/30 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center"><History className="h-5 w-5 text-white" /></div>
              <div><CardTitle>Historique</CardTitle><CardDescription>{filteredContributions.length} contribution(s){hasActiveFilters ? ` sur ${contributions.length}` : ''}</CardDescription></div>
            </div>
            {contributions.length > 0 && (
              <Button variant="outline" size="sm" className="gap-2 shrink-0" onClick={exportExcel} disabled={exporting} data-testid="export-excel-btn">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
                <span className="hidden sm:inline">{exporting ? 'Export...' : 'Excel'}</span>
              </Button>
            )}
          </div>

          {contributions.length > 0 && (
            <div className="space-y-3">
              {/* Recherche */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (montant, note, transaction...)" className="pl-9 h-9" data-testid="contribution-search" />
              </div>
              {/* Filtres par statut */}
              <div className="flex flex-wrap gap-1.5">
                {STATUS_FILTERS.map((s) => (
                  <button key={s.id} type="button" onClick={() => setStatusFilter(s.id)} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${statusFilter === s.id ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'}`}>{s.label}</button>
                ))}
              </div>
              {/* Filtres par type (uniquement ceux présents dans l'historique) */}
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setTypeFilter('all')} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${typeFilter === 'all' ? 'bg-foreground text-background border-foreground' : 'border-border hover:bg-muted'}`}>Tous les types</button>
                {[...contributionTypes, EVENT_CATEGORY].filter((t) => presentTypeKeys.has(t.id)).map((t) => (
                  <button key={t.id} type="button" onClick={() => setTypeFilter((prev) => (prev === t.id ? 'all' : t.id))} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors flex items-center gap-1 ${typeFilter === t.id ? 'bg-foreground text-background border-foreground' : 'border-border hover:bg-muted'}`}><span>{t.icon}</span>{t.label}</button>
                ))}
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {loading ? <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> : contributions.length === 0 ? (
            <div className="py-16 text-center"><div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4"><Wallet className="h-10 w-10 text-emerald-500" /></div><h3 className="text-xl font-semibold mb-2">Aucune contribution</h3><p className="text-muted-foreground mb-6">Commencez à contribuer</p><Button onClick={() => setDialogOpen(true)} className="gap-2"><Heart className="h-4 w-4" />Faire un don</Button></div>
          ) : filteredContributions.length === 0 ? (
            <div className="py-16 text-center"><div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4"><Search className="h-8 w-8 text-muted-foreground" /></div><h3 className="text-lg font-semibold mb-2">Aucun résultat</h3><p className="text-muted-foreground mb-4">Aucune contribution ne correspond à ces filtres</p><Button variant="outline" onClick={() => { setStatusFilter('all'); setTypeFilter('all'); setSearch(''); }} className="gap-2"><X className="h-4 w-4" />Réinitialiser les filtres</Button></div>
          ) : (
            <div className="divide-y">{filteredContributions.map((c) => { const t = getType(c.type); const m = getMethod(c.paymentMethod); const isEvent = !!c.eventId; const rowLabel = isEvent ? (c.eventTitle || 'Événement') : t.label; const rowIcon = isEvent ? '🎯' : t.icon; const rowColor = isEvent ? 'from-violet-500 to-purple-600' : t.color; return <div key={c.id} className="flex items-center justify-between p-4 md:p-5 hover:bg-muted/50 transition-colors" data-testid={`contribution-row-${c.id}`}><div className="flex items-center gap-3 md:gap-4 min-w-0"><div className={`w-11 h-11 md:w-12 md:h-12 rounded-2xl bg-gradient-to-br ${rowColor} flex items-center justify-center text-white text-xl shadow-sm shrink-0`}>{rowIcon}</div><div className="min-w-0"><p className="font-medium truncate">{rowLabel}</p><div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap"><span>{c.createdAt?.toDate?.() ? c.createdAt.toDate().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Récemment'}</span><span>•</span><Badge variant="outline" className="text-xs gap-1 px-2">{m.isImage ? <img src={m.icon} alt={m.label} className="h-4 w-auto" /> : m.icon} {m.label}</Badge>{c.receiptUrl && <button type="button" onClick={() => setReceiptViewUrl(c.receiptUrl)} className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:underline" data-testid={`view-receipt-${c.id}`}><Receipt className="h-3.5 w-3.5" />Reçu</button>}</div></div></div><div className="text-right shrink-0 pl-2"><p className="text-base md:text-lg font-bold whitespace-nowrap">{c.amount?.toLocaleString()} <span className="text-sm font-normal text-muted-foreground">F</span></p>{c.status === 'confirmed' ? <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1"><CheckCircle className="h-3 w-3" />Confirmé</Badge> : c.status === 'rejected' ? <Badge className="bg-red-500/10 text-red-600 border-red-500/20 gap-1"><XCircle className="h-3 w-3" />Rejeté</Badge> : <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 gap-1"><Clock className="h-3 w-3" />En attente</Badge>}</div></div> })}</div>
          )}
        </CardContent>
      </Card>

      {/* Visionneuse de reçu */}
      <Dialog open={!!receiptViewUrl} onOpenChange={(open) => !open && setReceiptViewUrl(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-emerald-500" />Reçu du paiement</DialogTitle></DialogHeader>
          {receiptViewUrl && (
            <div className="space-y-3">
              <img src={receiptViewUrl} alt="Reçu du paiement" className="w-full max-h-[60vh] rounded-lg border object-contain" />
              <a href={receiptViewUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-emerald-600 hover:underline"><ExternalLink className="h-4 w-4" />Ouvrir en plein écran</a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
